import { useCallback } from 'react';
import { Platform } from 'react-native';
import { getUser } from 'actions';
import { USER_SUCCESS } from 'actions/types';
import { LoadingAppViewModal } from 'design-system/LoadingAppView';
import { GET_CANCELLATION_OFFER_SUCCESS, GET_DOWNGRADE_OFFER_SUCCESS, getCancellationOffer, getDowngradeOffer, } from 'features/promotions/actions';
import { getStripeDiscountData } from 'features/promotions/hooks/useCancellationOffer';
import { selectPasscodeVisible } from 'reducers/selectors';
import { Amplitude } from 'utils';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import Modal from 'utils/packages/Modal';
import { isIOS } from '../../../constants';
import { getStripePrices, getStripeSubscriptionPurchasePreview, } from '../actions/stripe';
import { GET_STRIPE_SUBSCRIPTION_PURCHASE_PREVIEW_SUCCESS } from '../actions/types';
import { TierPeriod, isPremiumTier } from '../entities';
import { selectDidPayFullPrice } from '../selectors';
import { selectUserIsRefundable } from '../selectors/tier';
import { getStripeDowngradeDiscountData } from './useDowngradeOffer';
const logEligibility = (options) => {
    Amplitude.logEvent('Refund Eligibility Screen', {
        isEligible: options.length > 0,
        options: options.map(({ type }) => type),
    });
};
const strings = {
    loading: 'Checking your refund\neligibility...',
    errorTitle: 'Something went wrong',
    errorMessage: 'We couldn’t load your refund details. Please try again later.',
    close: 'Close',
};
/**
 * Entry point of the refund flow opened from the support chat. Loads everything
 * the refund screens need before navigating, so they never show a loading state.
 */
const useOpenRefundFlow = () => {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const navigation = useMainStackNavigation();
    const getPreview = useCallback(async (priceId, couponId) => {
        const res = await dispatch(getStripeSubscriptionPurchasePreview({
            priceId,
            couponId,
            isSubscriptionChange: true,
            prorationDate: new Date().toISOString(),
            isRefundFlow: true,
        }));
        return res?.type === GET_STRIPE_SUBSCRIPTION_PURCHASE_PREVIEW_SUCCESS
            ? res.payload
            : null;
    }, []);
    const getPriceMap = useCallback(async () => {
        if (!store.getState().stripe.priceMap) {
            await dispatch(getStripePrices());
        }
        return store.getState().stripe.priceMap;
    }, []);
    const getStripeOffer = useCallback(async (plan, priceMap) => {
        if (!selectDidPayFullPrice(store.getState())) {
            return null;
        }
        const offer = plan.period === TierPeriod.yearly
            ? await dispatch(getDowngradeOffer(true))
            : await dispatch(getCancellationOffer(Platform.OS === 'android' ? 'android' : 'ios'));
        if (offer?.type !== GET_DOWNGRADE_OFFER_SUCCESS &&
            offer?.type !== GET_CANCELLATION_OFFER_SUCCESS) {
            return null;
        }
        if (!offer.payload?.stripeOffers) {
            return null;
        }
        const parseOffers = offer.type === GET_DOWNGRADE_OFFER_SUCCESS
            ? getStripeDowngradeDiscountData
            : getStripeDiscountData;
        const discountData = parseOffers(offer.payload, priceMap).find((data) => data.type === 'stripe' &&
            data.subscription.tier === plan.tier &&
            data.subscription.period === plan.period);
        return discountData?.type === 'stripe' ? discountData : null;
    }, []);
    const getPartialRefundOption = useCallback(async (plan, priceMap) => {
        const discountData = await getStripeOffer(plan, priceMap);
        if (!discountData) {
            return null;
        }
        const preview = await getPreview(discountData.subscription.id, discountData.discount.discount.couponId);
        if (!preview?.refundableToday) {
            return null;
        }
        const netAnnualCost = plan.originalPayment - preview.refundableToday;
        const standardPrice = Number(priceMap[TierPeriod.monthly][plan.tier].price) * 12;
        const savingPercent = standardPrice
            ? Math.floor(((1 - netAnnualCost / standardPrice) * 100) / 5) * 5
            : 0;
        return {
            type: 'partial',
            refund: preview.refundableToday,
            discountData,
            standardPrice,
            savingPercent: savingPercent > 0 ? savingPercent : null,
        };
    }, [getPreview, getStripeOffer]);
    const getMonthlyOfferOption = useCallback(async (plan, priceMap) => {
        const discountData = await getStripeOffer(plan, priceMap);
        return discountData ? { type: 'monthlyOffer', discountData } : null;
    }, [getStripeOffer]);
    const getSwitchToMonthlyOption = useCallback(async (plan, priceMap) => {
        const subscription = priceMap[TierPeriod.monthly][plan.tier];
        const preview = await getPreview(subscription.id);
        if (!preview?.refundableToday) {
            return null;
        }
        const proRata = preview.prorationCredit || null;
        const credits = proRata
            ? Math.round((proRata - preview.nextChargePrice - preview.refundableToday) * 100) / 100
            : 0;
        return {
            type: 'switchToMonthly',
            refund: preview.refundableToday,
            subscription,
            monthlyPrice: preview.nextChargePrice,
            proRata,
            credits: credits > 0 ? credits : null,
        };
    }, [getPreview]);
    const navigateToEntryScreen = useCallback(async () => {
        const [userResult, priceMap] = await Promise.all([
            dispatch(getUser()),
            getPriceMap(),
        ]);
        if (userResult?.type !== USER_SUCCESS) {
            return false;
        }
        const state = store.getState();
        const { emmaProStatus } = state.user.user;
        const refundableAmount = selectUserIsRefundable(state);
        const { tier } = emmaProStatus;
        if (!refundableAmount?.amount ||
            emmaProStatus.store !== 'stripe' ||
            !isPremiumTier(tier)) {
            logEligibility([]);
            navigation.navigate('ManagePremiumSubscriptionRefundNotEligible');
            return true;
        }
        const plan = {
            tier: tier,
            period: emmaProStatus.period === 'yearly'
                ? TierPeriod.yearly
                : TierPeriod.monthly,
            currency: refundableAmount.currency,
            originalPayment: emmaProStatus.currentPrice?.amount ?? refundableAmount.amount,
            accessUntil: emmaProStatus.expiresOn,
        };
        const fullRefund = {
            type: 'full',
            refund: refundableAmount.amount,
        };
        let optionRequests;
        if (!priceMap) {
            optionRequests = [];
        }
        else if (plan.period === TierPeriod.yearly) {
            optionRequests = [
                getPartialRefundOption(plan, priceMap),
                getSwitchToMonthlyOption(plan, priceMap),
            ];
        }
        else {
            optionRequests = [getMonthlyOfferOption(plan, priceMap)];
        }
        const options = (await Promise.all(optionRequests.map((request) => request.catch(() => null)))).filter((option) => option !== null);
        if (!options.length) {
            logEligibility([fullRefund]);
            navigation.navigate('ManagePremiumSubscriptionRefundReview', {
                plan,
                option: fullRefund,
                isFlowEntry: true,
            });
            return true;
        }
        const allOptions = [...options, fullRefund];
        logEligibility(allOptions);
        navigation.navigate('ManagePremiumSubscriptionRefund', {
            plan,
            options: allOptions,
        });
        return true;
    }, [
        getMonthlyOfferOption,
        getPartialRefundOption,
        getPriceMap,
        getSwitchToMonthlyOption,
    ]);
    return useCallback(async () => {
        const showLoader = !selectPasscodeVisible(store.getState());
        if (showLoader) {
            LoadingAppViewModal.show({ title: strings.loading });
        }
        const didNavigate = await navigateToEntryScreen().catch(() => false);
        if (!didNavigate) {
            const showError = () => {
                Modal.showError(strings.errorMessage, undefined, strings.errorTitle, strings.close);
            };
            if (showLoader) {
                LoadingAppViewModal.hide(showError);
            }
            else {
                showError();
            }
            return;
        }
        if (showLoader) {
            setTimeout(() => LoadingAppViewModal.hide(), 
            // Waiting for navigation animation
            isIOS ? 300 : 100);
        }
    }, [navigateToEntryScreen]);
};
export default useOpenRefundFlow;
